import React, { useState } from "react";
import {
  CButton,
  CContainer,
  CFormInput,
  CFormLabel,
  CInputGroup,
} from '@coreui/react'
import { useNavigate, useLocation } from 'react-router-dom'
import { IoMdArrowRoundBack } from "react-icons/io";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import './style.css';

import connectorsData from './data/connectors.json';

import azureLogo from '../../assets/images/azure-logo.png';
import awsLogo from '../../assets/images/aws-logo.png';
import gcpLogo from '../../assets/images/gcp-logo.png';

const connectorImages = {
  'azure-logo.png': azureLogo,
  'aws-logo.png': awsLogo,
  'gcp-logo.png': gcpLogo,
};

const getInitials = (name) => {
  const words = name.split(' ').filter(Boolean);
  return ((words[0]?.[0] || '') + (words[1]?.[0] || '')).toUpperCase();
};

const SOURCE_TYPES = {
  API: 'api',
  UNC: 'unc',
  UPLOAD: 'upload',
};

const AUTH_TYPES = {
  TOKEN: 'token',
  BASIC: 'basic',
};

const RadioOption = ({ id, name, label, checked, onChange, style }) => (
  <label
    htmlFor={id}
    style={{
      display: 'flex', flexDirection: 'row', alignItems: 'center',
      cursor: 'pointer', ...style
    }}>
    <input
      type="radio"
      id={id}
      name={name}
      checked={checked}
      onChange={onChange}
      style={{ width: 16, height: 16, marginRight: 8, cursor: 'pointer', flexShrink: 0 }}
    />
    <span style={{ color: '#fff', fontSize: 14 }}>{label}</span>
  </label>
);

const ConfigureConnector = () => {

  const navigate = useNavigate();
  const location = useLocation();

  const connectorId = new URLSearchParams(location.search).get('connectorId');
  const connector = connectorsData.find(c => c.id === connectorId);
  const image = connector && connectorImages[connector.image];

  const [sourceType, setSourceType] = useState(SOURCE_TYPES.API);

  const [apiUrl, setApiUrl] = useState('');
  const [authType, setAuthType] = useState(AUTH_TYPES.TOKEN);
  const [apiToken, setApiToken] = useState('');
  const [basicUsername, setBasicUsername] = useState('');
  const [basicPassword, setBasicPassword] = useState('');

  const [uncPath, setUncPath] = useState('');

  const [uploadFile, setUploadFile] = useState(null);

  const handleSave = () => {
    toast.success('Connector configuration saved');
  };

  return (
    <CContainer style={{ marginBottom: 50 }}>

      <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>

        <h4>Configure Connector</h4>

        <CButton
          onClick={() => navigate('/connectors')}
          style={{
            marginBottom: '2%', borderWidth: 1, fontSize: 16,
            borderColor: '#fff', color: '#fff', background: 'transparent'
          }}
          color="primary"
          className="px-3"
        >
          <IoMdArrowRoundBack size={20} style={{ color: '#fff', marginRight: 10 }} />
          Back to Connectors
        </CButton>

      </div>

      {connector &&
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginBottom: 20 }}>

          {image ? (
            <img src={image} alt={connector.name} style={{ width: 36, height: 36, objectFit: 'contain' }} />
          ) : (
            <div style={{
              width: 36, height: 36, borderRadius: 6, backgroundColor: connector.color,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontSize: 13, fontWeight: 'bold'
            }}>
              {getInitials(connector.name)}
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', marginLeft: 12 }}>
            <span style={{ color: '#fff', fontSize: 16, fontWeight: 'bold' }}>{connector.name}</span>
            <span style={{ color: '#8a8fa3', fontSize: 11 }}>{connector.publisher}</span>
          </div>

        </div>
      }

      <div style={{ width: '100%', backgroundColor: '#252B3B', padding: 20, borderRadius: 15 }}>

        <CFormLabel style={{ color: 'white', fontWeight: 'bold' }}>Data Source</CFormLabel>

        <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', marginTop: 10, marginBottom: 20 }}>

          <RadioOption
            id="sourceTypeApi"
            name="sourceType"
            label="API"
            checked={sourceType === SOURCE_TYPES.API}
            onChange={() => setSourceType(SOURCE_TYPES.API)}
            style={{ marginRight: 30, marginBottom: 10 }}
          />

          <RadioOption
            id="sourceTypeUnc"
            name="sourceType"
            label="UNC Path"
            checked={sourceType === SOURCE_TYPES.UNC}
            onChange={() => setSourceType(SOURCE_TYPES.UNC)}
            style={{ marginRight: 30, marginBottom: 10 }}
          />

          <RadioOption
            id="sourceTypeUpload"
            name="sourceType"
            label="Upload"
            checked={sourceType === SOURCE_TYPES.UPLOAD}
            onChange={() => setSourceType(SOURCE_TYPES.UPLOAD)}
            style={{ marginBottom: 10 }}
          />

        </div>

        <hr style={{ borderColor: '#3a4258' }} />

        {sourceType === SOURCE_TYPES.API &&
          <div style={{ marginTop: 20 }}>

            <CFormLabel style={{ color: 'white' }}>URL</CFormLabel>
            <CInputGroup style={{ flexDirection: 'column' }}>
              <CFormInput
                placeholder="https://example.com/api"
                className="white-input"
                value={apiUrl}
                onChange={(e) => setApiUrl(e.target.value)}
                style={{ width: '100%' }}
              />
            </CInputGroup>

            <CFormLabel style={{ color: 'white', marginTop: 20 }}>Authentication</CFormLabel>

            <div style={{ display: 'flex', flexDirection: 'row', marginTop: 5, marginBottom: 15 }}>

              <RadioOption
                id="authTypeToken"
                name="authType"
                label="Token"
                checked={authType === AUTH_TYPES.TOKEN}
                onChange={() => setAuthType(AUTH_TYPES.TOKEN)}
                style={{ marginRight: 30 }}
              />

              <RadioOption
                id="authTypeBasic"
                name="authType"
                label="Basic Credentials"
                checked={authType === AUTH_TYPES.BASIC}
                onChange={() => setAuthType(AUTH_TYPES.BASIC)}
              />

            </div>

            {authType === AUTH_TYPES.TOKEN &&
              <CInputGroup style={{ flexDirection: 'column' }}>
                <CFormInput
                  placeholder="Token"
                  type="password"
                  className="white-input"
                  value={apiToken}
                  onChange={(e) => setApiToken(e.target.value)}
                  style={{ width: '100%' }}
                />
              </CInputGroup>
            }

            {authType === AUTH_TYPES.BASIC &&
              <>
                <CInputGroup style={{ flexDirection: 'column', marginBottom: 15 }}>
                  <CFormInput
                    placeholder="Username"
                    className="white-input"
                    value={basicUsername}
                    onChange={(e) => setBasicUsername(e.target.value)}
                    style={{ width: '100%' }}
                  />
                </CInputGroup>

                <CInputGroup style={{ flexDirection: 'column' }}>
                  <CFormInput
                    placeholder="Password"
                    type="password"
                    className="white-input"
                    value={basicPassword}
                    onChange={(e) => setBasicPassword(e.target.value)}
                    style={{ width: '100%' }}
                  />
                </CInputGroup>
              </>
            }

          </div>
        }

        {sourceType === SOURCE_TYPES.UNC &&
          <div style={{ marginTop: 20 }}>

            <CFormLabel style={{ color: 'white' }}>Folder Path</CFormLabel>
            <CInputGroup style={{ flexDirection: 'column' }}>
              <CFormInput
                placeholder="\\server\share\folder"
                className="white-input"
                value={uncPath}
                onChange={(e) => setUncPath(e.target.value)}
                style={{ width: '100%' }}
              />
            </CInputGroup>

          </div>
        }

        {sourceType === SOURCE_TYPES.UPLOAD &&
          <div style={{ marginTop: 20 }}>

            <CFormLabel style={{ color: 'white' }}>File</CFormLabel>
            <CInputGroup style={{ flexDirection: 'column' }}>
              <CFormInput
                type="file"
                className="white-input"
                onChange={(e) => setUploadFile(e.target.files[0])}
                style={{ width: '100%' }}
              />
            </CInputGroup>

          </div>
        }

        <CButton
          style={{
            width: '100%', marginTop: 30, borderWidth: 0, fontSize: 20, background: '#89181b'
          }}
          color="primary"
          className="px-3"
          onClick={handleSave}
        >
          Save
        </CButton>

      </div>

    </CContainer>
  );
};

export default ConfigureConnector;
